/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * What a new household still needs to do.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * A brand-new user used to land on a dashboard reading "Nothing planned for
 * today yet", with an empty ideas card, no suggestions and no shopping list.
 * Every panel was technically correct and the screen said nothing about what to
 * do next — which is the difference between an app someone adopts and one they
 * open once and forget.
 *
 * The steps are in the only order that works: you cannot plan a meal before you
 * have a recipe, and a shopping list built from an empty plan is an empty list.
 * It disappears on its own once all four are done — there is no "dismiss",
 * because dismissing it would hide the fact that the app cannot yet do the
 * thing it is for.
 *
 * ── ITS ROLE CHANGED ────────────────────────────────────────────────────────
 * It used to be the only piece of journey guidance in the app, and it carried
 * that alone — which it couldn't, because it vanishes permanently at 4/4,
 * exactly when the user graduates from "how do I set up" to "what do I do this
 * week".
 *
 * NextCard now leads with the single next action. This became the map beneath
 * it: all four steps, how far through you are, and the freedom to jump ahead if
 * you'd rather do them out of order. Two components, two jobs — recommend, and
 * show the whole picture.
 */

import { Link } from "react-router-dom";

import type { SetupState } from "../../api/types.ts";
import { Icon } from "../../components/Icon.tsx";

/**
 * Where each step goes.
 *
 * `eaters` used to point at /settings, which put a brand-new non-technical user
 * into a screen of meal slots, pantry staples and circle toggles as their first
 * experience of the product. It now points at Household, which leads with the
 * eaters list and nothing else.
 *
 * `recipes` and `plan` carry a query param that opens the relevant picker on
 * arrival: a step should end in the doing, not merely in the arriving.
 */
const WHERE: Record<string, { to: string; cta: string }> = {
  eaters: { to: "/family", cta: "Add people" },
  recipes: { to: "/recipes?add=1", cta: "Add a recipe" },
  plan: { to: "/plan?add=1", cta: "Open the week" },
  shopping: { to: "/shopping?generate=1", cta: "Build a list" },
};

export function GettingStarted({ setup }: { setup: SetupState }) {
  if (setup.remaining === 0) return null;

  const done = setup.steps.length - setup.remaining;

  return (
    <section className="card p-5">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h2 className="type-section">Getting started</h2>
        <span className="label-mono text-ink-3">{done} of {setup.steps.length}</span>
      </div>
      <p className="mb-4 text-sm text-ink-2">
        Four things and the planner can do its job. Each one takes a minute.
      </p>

      {/* A quiet progress bar rather than a second accent-tinted panel: the
          NextCard directly above is already the loud element, and two competing
          call-to-action colours on one screen is how you get neither. */}
      <div className="mb-4 h-1 overflow-hidden rounded-full bg-paper-3"
           role="progressbar" aria-valuenow={done} aria-valuemin={0}
           aria-valuemax={setup.steps.length}>
        <div className="h-full bg-accent transition-all"
             style={{ width: `${(done / setup.steps.length) * 100}%` }} />
      </div>

      <ol className="space-y-2">
        {setup.steps.map((step) => {
          const target = WHERE[step.key];
          const isNext = setup.next === step.key;
          return (
            <li key={step.key}
                className={`flex items-start gap-3 rounded-lg border p-3 ${
                  step.done ? "border-rule-soft opacity-60"
                  : isNext ? "border-accent/50 bg-accent/5"
                  : "border-rule-soft"}`}>
              <Icon
                name={step.done ? "check" : "circle"}
                className={`mt-0.5 h-4 w-4 shrink-0 ${step.done ? "text-ok" : "text-ink-3"}`}
              />
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-medium ${step.done ? "line-through text-ink-3" : "text-ink"}`}>
                  {step.title}
                </div>
                {!step.done && <div className="mt-0.5 text-xs text-ink-3">{step.why}</div>}
              </div>
              {!step.done && target && (
                // Every step is a ghost button now, including the next one.
                // NextCard is the recommendation; this list is the map, and a
                // map that shouts is just a second recommendation.
                <Link to={target.to} className="btn-ghost shrink-0 text-xs">
                  {target.cta}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
